import { gofarService } from './gofar-service';
import { HTTP_METHODS, HTTP_STATUS_CODES } from './config';

const SHIPPING_OPTIONS = {
  AUSTRALIA_POST: 'shipping-australia-post',
  INTERNATIONAL_SHIPPING: 'international-shipping-with-tracking'
};

const COUNTRY_SETTINGS = {
  AU: { currencyCode: 'AUD', currencySymbol: '$', exchangeRate: 1, shippingOption: SHIPPING_OPTIONS.AUSTRALIA_POST },
  CA: {
    currencyCode: 'CAD',
    currencySymbol: '$',
    exchangeRate: 0.9,
    shippingOption: SHIPPING_OPTIONS.INTERNATIONAL_SHIPPING
  },
  US: {
    currencyCode: 'USD',
    currencySymbol: '$',
    exchangeRate: 0.7,
    shippingOption: SHIPPING_OPTIONS.INTERNATIONAL_SHIPPING
  },
  DEFAULT: { currencyCode: 'AUD', currencySymbol: '$', exchangeRate: 1, shippingOption: SHIPPING_OPTIONS.INTERNATIONAL_SHIPPING },
};

/**
 * Unfortunately chargebee doesn't allow us to store images with addons
 */
const PRODUCT_IMAGES = {
  RAY: require('../assets/img/ray_small.png'),
  ADAPTER: require('../assets/img/adapter.png'),
  SUBSCRIPTION: require('../assets/img/subscription.png')
};

export const getCountrySetting = (countryCode) => {
  const countrySetting = COUNTRY_SETTINGS[countryCode] || COUNTRY_SETTINGS['DEFAULT'];
  return countrySetting;
};

export const getChargebeeItems = async () => {
  const [products, plans, shippingOptions] = await Promise.all([
    gofarService('/api/checkout/products', HTTP_METHODS.GET),
    gofarService('/api/checkout/subscription-plans', HTTP_METHODS.GET),
    gofarService('/api/checkout/shipping-methods', HTTP_METHODS.GET)
  ]);
  return [products.data, plans.data, shippingOptions.data];
};
const getProductImage = (productId) => {
  return productId.includes('adapter') ? PRODUCT_IMAGES.ADAPTER : PRODUCT_IMAGES.RAY;
};

const convertToCurrency = (amount, countrySetting) => {
  return Math.ceil(parseFloat(amount) * countrySetting.exchangeRate);
};

const roundTo100th = (amount, countrySetting)=> {
  if(countrySetting.currencyCode !== 'AUD'){
    return Math.round(amount / 100) * 100;
  }
  return amount;
  
}

const getShippingOption = (countrySetting) => {
  return countrySetting.shippingOption;
};

const calculatePriceWithDiscount = (price, coupon) => {
  const discount_amount = coupon.discount_type === 'fixed_amount' ? coupon.discount_amount : (price * coupon.discount_percentage) / 100;
  if(discount_amount > price){
    return 0;
  }
  return price - discount_amount;
};

const checkIfDiscountIsApplicableForProduct = (selectedPlan, product, coupon, isDiscountAppliedAlready) => {
  if(!coupon.isValid){
    return false;
  }
  if(isDiscountAppliedAlready){
    return false;
  }
  if(coupon.apply_on === 'invoice_amount'){
    return false;
  }
  if(!coupon.meta_data?.plan_ids?.includes(selectedPlan)){
    return false;
  }
  if(coupon.addon_constraint === 'none'){
    return false;
  }
  if(coupon.addon_constraint === 'specific' && !coupon.addon_ids?.includes(product)){
    return false;
  }
  return true;
}

const mapToSubscriptionProducts = (selectedProducts, chargebeeProducts, countrySetting, coupon, quantity, selectedPlan)=> {
  let isDiscountAppliedAlready = false;
  const isApplicableToManyProducts = coupon.meta_data?.applicableToMany || false;
  const subscriptionProducts = selectedProducts.map((product) => {
    const chargebeeProduct = chargebeeProducts.find((chargebeeProduct) => chargebeeProduct.id === product);
    const productImage = getProductImage(product);
    const originalPrice = chargebeeProduct.price;
    const priceInSelectedCurrency = roundTo100th(convertToCurrency(originalPrice, countrySetting), countrySetting) / 100;
    const isDiscountApplicableForProduct = checkIfDiscountIsApplicableForProduct(selectedPlan, product, coupon, isDiscountAppliedAlready);
    // only one add_ons will be selected while creating coupon making sure that this process doesn't apply same coupon on both adapter and ray
    const discountedPrice = isDiscountApplicableForProduct || isApplicableToManyProducts ? calculatePriceWithDiscount(originalPrice, coupon, countrySetting) : originalPrice;
    isDiscountAppliedAlready = discountedPrice < originalPrice;
    const discountedPriceInSelectedCurrency = roundTo100th(convertToCurrency(discountedPrice, countrySetting), countrySetting) / 100;
    return {
      id: chargebeeProduct.id,
      name: chargebeeProduct.name,
      image: productImage,
      quantity,
      originalPrice,
      price: discountedPrice,
      priceInSelectedCurrency,
      discountedPriceInSelectedCurrency
    };
  });
  return subscriptionProducts;
}

const mapToSubscriptionPlan = (selectedPlan, chargebeePlans, countrySetting, coupon, quantity, isDiscountAppliedOnProducts)=> {
  const chargebeePlan = chargebeePlans.find((plan) => plan.id === selectedPlan);
  // original price will always have a round figure in hundreds
  const originalPrice = chargebeePlan.price;
  // convert them in round figure of 100th to have better pricing
  const priceInSelectedCurrency = roundTo100th(convertToCurrency(originalPrice, countrySetting), countrySetting) / 100;

  const discountedPrice =
    !isDiscountAppliedOnProducts && coupon.apply_on === 'each_specified_item' && coupon.plan_ids?.includes(chargebeePlan.id)
      ? calculatePriceWithDiscount(originalPrice, coupon, countrySetting)
      : originalPrice;
  const discountedPriceInSelectedCurrency = roundTo100th(convertToCurrency(discountedPrice, countrySetting), countrySetting) / 100;
  const subscriptionPlan = {
    id: chargebeePlan.id,
    name: chargebeePlan.name,
    image: PRODUCT_IMAGES.SUBSCRIPTION,
    quantity,
    originalPrice,
    price: discountedPrice,
    priceInSelectedCurrency,
    discountedPriceInSelectedCurrency
  };
  return subscriptionPlan;

}



const mapToSelectedShippingOption = (chargebeeShippingOptions, countrySetting)=> {
  const shippingOptionForCountry = getShippingOption(countrySetting);
  const chargebeeShippingOption = chargebeeShippingOptions.find(
    (shippingOption) => shippingOption.id === shippingOptionForCountry
  );
  const originalShippingPrice = chargebeeShippingOption.price;
  // discount will not be applicable in shipping
  const selectedShippingOption = {
    id: chargebeeShippingOption.id,
    name: chargebeeShippingOption.name,
    price: originalShippingPrice,
    priceInSelectedCurrency: roundTo100th(convertToCurrency(originalShippingPrice, countrySetting), countrySetting) / 100
  };
  return selectedShippingOption;
}

const getTotalDiscountedAmount = (plan, products, isDiscountAppliedOnPlan)=> {
  if(isDiscountAppliedOnPlan){
    return plan.originalPrice - plan.price;
  }
  const productsTotalOriginalAmount = products.reduce(
    (total, product) => total + product.originalPrice * product.quantity,
    0
  );
  const productsTotalDiscountedAmount = products.reduce(
    (total, product) => total + product.price * product.quantity,
    0
  );

  return productsTotalOriginalAmount - productsTotalDiscountedAmount;
}

export const mapToSubscriptionCheckout = (
  selectedProducts,
  chargebeeProducts,
  selectedPlan,
  chargebeePlans,
  subscription,
  chargebeeShippingOptions,
  coupon,
  countrySetting,
  selectedQuantity
) => {
  /**
   * TODO: should be able to have different quantities for Subscription, Ray and Adapter in future
   */
  const MAX_ALLOWED_QUANTITY = 9;
  const quantity =
    parseInt(selectedQuantity, 10) > MAX_ALLOWED_QUANTITY ? MAX_ALLOWED_QUANTITY : parseInt(selectedQuantity, 10);

  const products = mapToSubscriptionProducts(selectedProducts, chargebeeProducts, countrySetting, coupon, quantity, selectedPlan);
  const productsTotalAmount = products.reduce(
    (total, product) => total + product.originalPrice * product.quantity,
    0
  );
  const productsTotalDiscountedAmount = products.reduce((total, product)=> total + product.price * product.quantity, 0);
  const productsTotalAmountInSelectedCurrency = products.reduce(
    (total, product) => total + (product.priceInSelectedCurrency * product.quantity),
    0
  );

  const isDiscountAppliedOnProducts = productsTotalDiscountedAmount < productsTotalAmount;

  const plan = mapToSubscriptionPlan(selectedPlan, chargebeePlans, countrySetting, coupon, quantity, isDiscountAppliedOnProducts);
  const isDiscountAppliedOnPlan =  plan.price < plan.originalPrice;

  const planTotalAmount = plan.originalPrice * plan.quantity;
  const planTotalAmountInSelectedCurrency = plan.priceInSelectedCurrency * plan.quantity;
  
  

  const selectedShippingOption = mapToSelectedShippingOption(chargebeeShippingOptions, countrySetting);

  const originalSubTotal = planTotalAmount + productsTotalAmount;
  const originalSubTotalInSelectedCurrency = planTotalAmountInSelectedCurrency +  productsTotalAmountInSelectedCurrency;

  const totalDiscountedAmount = getTotalDiscountedAmount(plan, products, isDiscountAppliedOnPlan);
  const totalDiscountedAmountInSelectedCurrency = convertToCurrency(totalDiscountedAmount, countrySetting) / 100; 


  const originalSubTotalWithDiscount = (originalSubTotal - totalDiscountedAmount) / 100;
  const originalSubTotalWithDiscountInSelectedCurrency = originalSubTotalInSelectedCurrency - totalDiscountedAmountInSelectedCurrency; 


  const discountedSubTotal =
    coupon.apply_on === 'invoice_amount'
      ? calculatePriceWithDiscount(originalSubTotal, coupon, countrySetting)
      : originalSubTotal;
  const discountedSubTotalInSelectedCurrency = convertToCurrency(discountedSubTotal, countrySetting) / 100;


  const grandTotal = coupon.apply_on === 'invoice_amount' ? discountedSubTotal + selectedShippingOption.price : originalSubTotalWithDiscount * 100 + selectedShippingOption.price;

  const grandTotalInSelectedCurrency = coupon.apply_on === 'invoice_amount'? discountedSubTotalInSelectedCurrency + selectedShippingOption.priceInSelectedCurrency: originalSubTotalWithDiscountInSelectedCurrency + selectedShippingOption.priceInSelectedCurrency;

  const message = discountedSubTotal < originalSubTotal ? `Coupon ${coupon.code} has been applied.`: '';
  
  const updatedCoupon = {
    ...coupon,
    message
  }

  const result = {
    ...subscription,
    products,
    plan,
    selectedShippingOption,
    quantity,
    originalSubTotal,
    originalSubTotalInSelectedCurrency,
    originalSubTotalWithDiscount,
    originalSubTotalWithDiscountInSelectedCurrency,
    subTotal: discountedSubTotal,
    subTotalInSelectedCurrency: discountedSubTotalInSelectedCurrency,
    grandTotal,
    grandTotalInSelectedCurrency,
    coupon: updatedCoupon
  };
  return result;
};

export const validateCouponCode = async (couponCode, planName, addOns) => {
  if (!couponCode) {
    return {
      isValid: false
    };
  }
  if(planName.includes('basic')){
    return {
      isValid: false,
      message: 'Coupon is not available for Basic Plan.'
    };
  }
  if(couponCode.toLowerCase().includes('grandfathered')){
    return {
      isValid: false,
      message: 'Invalid Coupon Code'
    };
  }

  const coupon_result = await gofarService(`/api/checkout/validate-couponcode`, HTTP_METHODS.POST, {
    couponCode: couponCode,
    planName,
    addOns
  });
  return {
    ...coupon_result.data,
    isValid: !coupon_result.data.message
  };
};

export const createSubscription = async (customer, subscription, coupon, paymentData, paypalExpress = {}) => {
  const subscriptionProducts = subscription.products.map((p) => {
    return { id: p.id, quantity: subscription.quantity };
  });
  const productShipping = {id: subscription.selectedShippingOption.id, quantity: 1 };
  const subscriptionPayload = {
    ...paypalExpress,
    plan_id: subscription.plan.id,
    auto_collection: 'on',
    payment_intent: paymentData,
    plan_quantity: subscription.quantity,
    coupon_ids: [coupon.code],
    // Will be used later to get list of codes
    parentCode: coupon.parentCode,
    parentCodeApplyOn: coupon.apply_on,
    discountType: coupon.discount_type,
    // Chargebee Order reporter scheduler later uses this info to list all subscription made via website
    meta_data: {
      order_source: 'chargebee'
    },
    billing_address: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      company: customer.companyName,
      line1: customer.address,
      line2: customer.apartment,
      line3: customer.suburb,
      state_code: customer.stateCode,
      state: customer.state,
      city: customer.city,
      zip: customer.postalCode,
      country: customer.country
    },
    // as part of phase 1, we will use same address as both billing and shipping
    // we will later allow user to set different addresses for them
    shipping_address: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      company: customer.companyName,
      line1: customer.address,
      line2: customer.apartment,
      line3: customer.suburb,
      // disable state code since it's enable for selected countries only.
      // https://apidocs.chargebee.com/docs/api/addresses#address_state_code
      // state_code: customer.stateCode,
      state: customer.state,
      city: customer.city,
      zip: customer.postalCode,
      country: customer.country
    },
    customer: {
      first_name: customer.firstName,
      last_name: customer.lastName,
      email: customer.email,
      locale: 'en-US',
      company: customer.company,
      phone: customer.phone
    },
    addons: [...subscriptionProducts, productShipping]
  };
  const chargebee_subscription_result = await gofarService('/api/checkout/chargebee/subscription', HTTP_METHODS.POST, subscriptionPayload);
  return chargebee_subscription_result;
};

export const fetchPaypalCustomerDetail = async (billingAgreementId, customer) => {
  const paypal_customer_result = await gofarService(
    `/api/checkout/paypal-customer-data?billingAgreementId=${billingAgreementId}`,
    HTTP_METHODS.GET
  );
  if (paypal_customer_result.status !== HTTP_STATUS_CODES.SUCCESS) {
    return customer;
  }
  const paypalCustomer = paypal_customer_result.data;
  const gofarCustomer = {
    firstName: paypalCustomer.firstName.length > 0 ? paypalCustomer.firstName : customer.firstName,
    lastName: paypalCustomer.lastName.length > 0 ? paypalCustomer.lastName : customer.lastName,
    address: paypalCustomer.address_line1.length > 0 ? paypalCustomer.address_line1 : customer.address,
    apartment: paypalCustomer.address_line2.length > 0 ? paypalCustomer.address_line2 : customer.address,
    city: paypalCustomer.city.length > 0 ? paypalCustomer.city : customer.city,
    stateCode: paypalCustomer.state.length > 0 ? paypalCustomer.state : customer.stateCode,
    postalCode: paypalCustomer.zip.length > 0 ? paypalCustomer.zip : customer.postalCode,
    country: paypalCustomer.countryCode.length > 0 ? paypalCustomer.countryCode : customer.country,
    email: paypalCustomer.email.length > 0 ? decodeURIComponent(paypalCustomer.email) : customer.email
  };
  return gofarCustomer;
};


export const getGTMSettings = (env)=> {
  return {
    code: env === 'production'? 'GTM-KBDDH6B' : 'GTM-W23LBBX2',
    site: env === 'production' ? 'gofar' : 'gofar-test'
  };
}

export const getGTMInnerHTML = (GTMSettingsCode)=> {
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${GTMSettingsCode}');`;
}